import { FragmentType, graphql, useFragment } from '@/generated/gql'
import React, { useCallback } from 'react'
import {
  canteenMenuItemsMenuItemDataFragment,
  canteenMenuItemsVendorDataFragment,
} from './MenuBody/MenuItems'
import { CartMappingOptionInput } from './MenuBody/cartMapping'
import OrderAgainSection, { OrderAgainSectionItem } from './OrderAgain/OrderAgainSection'

const canteenOrderAgainMenuDataFragment = graphql(`
  fragment CanteenOrderAgain_MenuDataFragment on CCMealService {
    orderAgainItems {
      itemReference
      orderCount
      averageRating
      vendor {
        id
        companyName
        logo
      }
      menuItemVariation {
        _id
        name
        priceCustomerFacing
        orderLimit
        coreLiveItem {
          _id
          name
          image
          dietaries
          allergens
          subAllergens {
            nuts
            cereals
          }
          options {
            _id
            minQty
            maxQty
            qtyPerPerson
          }
        }
      }
    }
  }
`)

export type OrderAgainMenuItemLookup = Map<
  string,
  {
    item: FragmentType<typeof canteenMenuItemsMenuItemDataFragment>
    vendor: FragmentType<typeof canteenMenuItemsVendorDataFragment>
  }
>

type Props = {
  menuData: FragmentType<typeof canteenOrderAgainMenuDataFragment>
  hidePrices: boolean
  searchTerm?: string
  // Keyed by itemReference (== liveVariation._id) — the same items already
  // loaded for today's main menu, so add-to-cart/show-options go through the
  // exact same data and handlers as a normal menu item.
  menuItemLookup: OrderAgainMenuItemLookup
  isAddItemDisabled: (
    incomingId: string,
    orderLimit: number | null,
    quantityRemaining: number | null,
  ) => boolean
  onAddToCart: (
    itemData: FragmentType<typeof canteenMenuItemsMenuItemDataFragment>,
    ccVariationId: string,
    quantity: number,
    options: Array<CartMappingOptionInput>,
    liveVariationQuantityData: { orderLimit: number | null; quantityRemaining: number | null },
  ) => void
  onSeeMore: (
    itemId: string,
    vendorId: string,
    itemData: FragmentType<typeof canteenMenuItemsMenuItemDataFragment>,
  ) => void
}

const OrderAgainGraphql: React.FC<Props> = ({
  menuData: menuDataProp,
  hidePrices,
  searchTerm = '',
  menuItemLookup,
  isAddItemDisabled,
  onAddToCart,
  onSeeMore,
}) => {
  const menuData = useFragment(canteenOrderAgainMenuDataFragment, menuDataProp)

  const items: OrderAgainSectionItem[] = menuData.orderAgainItems.map((item) => ({
    itemReference: item.itemReference,
    orderCount: item.orderCount,
    averageRating: item.averageRating,
    vendorId: item.vendor.id,
    vendorName: item.vendor.companyName,
    vendorLogo: item.vendor.logo ?? null,
    name: item.menuItemVariation.coreLiveItem?.name ?? item.menuItemVariation.name,
    image: item.menuItemVariation.coreLiveItem?.image ?? null,
    priceCustomerFacing: item.menuItemVariation.priceCustomerFacing,
    dietaries: item.menuItemVariation.coreLiveItem?.dietaries ?? [],
    allergens: item.menuItemVariation.coreLiveItem?.allergens ?? [],
    subAllergens: item.menuItemVariation.coreLiveItem?.subAllergens ?? { nuts: [], cereals: [] },
    options: item.menuItemVariation.coreLiveItem?.options ?? null,
  }))

  // Only items that still resolve against already-loaded menu can
  // actually be added to the cart (e.g. the user's own active allergen/vendor
  // filters could exclude one) — hide those rather than offering a broken action.
  const availableItems = items
    .filter((item) => menuItemLookup.has(item.itemReference))
    .filter((item) =>
      searchTerm.trim()
        ? (item.name ?? '').toLowerCase().includes(searchTerm.toLowerCase().trim())
        : true,
    )

  const disabledItemReferences = new Set<string>()
  for (const item of availableItems) {
    const match = menuItemLookup.get(item.itemReference)
    if (!match) continue
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const data = useFragment(canteenMenuItemsMenuItemDataFragment, match.item)
    if (
      isAddItemDisabled(data.liveMenuItem._id, data.liveVariation.orderLimit, data.quantityRemaining)
    ) {
      disabledItemReferences.add(item.itemReference)
    }
  }

  const handleAddToCart = useCallback(
    (itemReference: string) => {
      const match = menuItemLookup.get(itemReference)
      if (!match) return
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const data = useFragment(canteenMenuItemsMenuItemDataFragment, match.item)
      onAddToCart(match.item, data.liveVariation._id, 1, [], {
        orderLimit: data.liveVariation.orderLimit ?? null,
        quantityRemaining: data.quantityRemaining,
      })
    },
    [menuItemLookup, onAddToCart],
  )

  const handleShowOptions = useCallback(
    (itemReference: string) => {
      const match = menuItemLookup.get(itemReference)
      if (!match) return
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const data = useFragment(canteenMenuItemsMenuItemDataFragment, match.item)
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const vendor = useFragment(canteenMenuItemsVendorDataFragment, match.vendor)
      onSeeMore(data.liveMenuItem._id, vendor.id, match.item)
    },
    [menuItemLookup, onSeeMore],
  )

  return (
    <OrderAgainSection
      items={availableItems}
      hidePrices={hidePrices}
      disabledItemReferences={disabledItemReferences}
      onAddToCart={handleAddToCart}
      onShowOptions={handleShowOptions}
    />
  )
}

export default OrderAgainGraphql
